import { Spinner, Alert, Button, Form, ActionGroup } from '@patternfly/react-core';
import React, { ReactElement, useState } from 'react';
import './LoginPage.scss';
import CenteredPage from '../../../layout/CenteredPage';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import Loader from '../../../components/util/Loader';
import { OtpInput } from './OtpInput';
import { LoginMFA as MFA_Login, ResendMFA } from '../../../api/login/Login';
import { getCSRFToken } from '../../../api/security/CSRFToken';
import axiosConfig from '../../../api/axiosConfig';
import { AxiosError } from 'axios';

interface Props {
	isSSO?: boolean;
}

// Maximum number of OTP entry attempts allowed before the user must request a new code.
const MAX_OTP_ATTEMPTS = 3;

export default function LoginMFA(props: Props): ReactElement {
	const [otp, setOtp] = useState('');
	const [loading, setLoading] = useState(true);
	const [btnLoading, setBtnLoading] = useState(false);
	const [message, setMessage] = useState<React.ReactElement | null>();
	const [failedOtp, setFailedOtp] = useState(false);
	// tracks how many invalid OTP submissions have occurred. Only once this
	// reaches MAX_OTP_ATTEMPTS does the user get locked out and required to
	// request a new code (failedOtp) — before that, they can freely correct and
	// resubmit without needing to hit "Resend".
	const [otpAttempts, setOtpAttempts] = useState(0);
	const navigate = useNavigate();
	const location = useLocation();
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const mfaToken = location.state?.mfa_token as string;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const userEmail = location.state?.email as string;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	const userTokenExpiry = location.state?.token_expiry as number;

	useMount(() => {
		void getCSRFToken().then((token) => {
			axiosConfig.instance.defaults.headers['CSRF-TOKEN'] = token;
		});

		setTimeout(() => {
			setLoading(false);
		}, 1250);
	});

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			void verifyOtp();
		}
	};

	const verifyOtp = () => {
		setBtnLoading(true);

		if (otp === '' || otp === '') {
			setMessage(
				<Alert
					variant="danger"
					isInline
					isPlain
					title="Verification Code must be set"
				/>
			);
			setLoading(false);
			setBtnLoading(false);
			return;
		}

		if (otp && otp.length != 6) {
			setMessage(
				<Alert
					variant="danger"
					isInline
					isPlain
					title="Verification Code must be exactly 6 digits"
				/>
			);

			setLoading(false);
			setBtnLoading(false);
			return;
		}

		void MFA_Login(mfaToken, otp)
			.then((response) => {
				if (response.pinnedDashboardId) {
					navigate(`/analyze/dashboards/view/${response.pinnedDashboardId}`);
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				} else if (location.state && location.state.nextPath) {
					// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
					navigate(location.state.nextPath);
				} else {
					navigate('/getting-started');
				}
			})
			.catch((error: AxiosError) => {
				console.error('login MFA error', error);

				// UPDATED: count this failed attempt. Only lock the user out
				// (hide input, disable submit, force a new OTP request) once
				// they've exhausted MAX_OTP_ATTEMPTS — otherwise let them
				// correct and resubmit immediately.
				const attemptsSoFar = otpAttempts + 1;
				setOtpAttempts(attemptsSoFar);

				const attemptsRemaining = MAX_OTP_ATTEMPTS - attemptsSoFar;
				const hasAttemptsRemaining = attemptsRemaining > 0;

				setFailedOtp(!hasAttemptsRemaining);

				// Clear the entered code so the user isn't stuck resubmitting
				// the same invalid value.
				setOtp('');

				setMessage(
					<Alert
						variant="danger"
						isInline
						isPlain
						title={
							hasAttemptsRemaining
								? `The entered OTP is incorrect. You have ${attemptsRemaining} attempt${
										attemptsRemaining === 1 ? '' : 's'
								  } remaining.`
								: 'The entered OTP is incorrect. Please go back and generate a new OTP Code'
						}
					/>
				);

				setLoading(false);
				setBtnLoading(false);
			});
	};

	const resendOtp = () => {
		void ResendMFA(mfaToken).then((response) => {
			// requesting a new code resets the attempt count and
			// unlocks the input, since the user now has a fresh OTP to try.
			setOtpAttempts(0);
			setFailedOtp(false);
			setOtp('');

			setMessage(
				<Alert
					variant="success"
					isInline
					isPlain
					title="OTP request has succesfully been re-sent"
				/>
			);
		});
	};

	const verifyButton = loading ? (
		<div className="spinner-container">
			<Spinner size="lg" />
		</div>
	) : btnLoading ? (
		<Loader />
	) : (
		<Button
			onClick={verifyOtp}
			variant="primary"
			data-testid="mfa-submit"
			type="button"
			className="btn-login"
			isDisabled={failedOtp}
		>
			Verify Code
		</Button>
	);

	return (
		<CenteredPage
			description={
				loading
					? ''
					: `We have sent a 6-digit verification code to your email address ${
							userEmail ? `(${userEmail})` : ''
					  }. Please enter it below to continue.`
			}
			notice={message}
		>
			{loading && (
				<div className="spinner-container">
					<Spinner size="lg" />
					<p>Sending verification code…</p>
				</div>
			)}

			{!loading && (
				<Form autoComplete="one-time-code">
					{!failedOtp && (
						<OtpInput
							// Force remount on each failed attempt so OtpInput's internal
							// input state fully resets in sync with otp being cleared above.
							key={otpAttempts}
							onChange={(otp) => setOtp(otp)}
							userTokenExpiry={userTokenExpiry}
						/>
					)}
					<ActionGroup className="actiongroup-center">{verifyButton}</ActionGroup>
					<br />
					<div className="text-center">
						<Button
							className="text-center"
							variant="link"
							isInline
							onClick={() => {
								navigate('/login');
							}}
							data-testid="mfa-back"
						>
							<FontAwesomeIcon icon={faArrowLeft} /> Go back
						</Button>
						<br />
						<Button
							variant="link"
							isInline
							onClick={resendOtp}
							data-testid="mfa-resend"
						>
							Didn't receive the code? Resend
						</Button>
					</div>
				</Form>
			)}
		</CenteredPage>
	);
}
